import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Search, RotateCcw, AlertCircle } from "lucide-react";
import { workshopsService } from "@/api/workshopsService";
import { WORKSHOP_CATEGORIES } from "@/types/workshop";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Reveal } from "@/components/site/SiteLayout";

import WorkshopHero from "@/components/workshops/WorkshopHero";
import FeaturedWorkshop from "@/components/workshops/FeaturedWorkshop";
import WorkshopFilters from "@/components/workshops/WorkshopFilters";
import WorkshopCard from "@/components/workshops/WorkshopCard";

/**
 * ============================================================
 * PUBLIC WORKSHOPS CATALOG PAGE (/workshops)
 * ============================================================
 * Premium technical education catalog:
 * - Technical value-first hero
 * - Flagship featured upcoming session
 * - Instant search and multi-dimension filtering
 * - High-aesthetic GlassCard workshop grid
 */
export default function Workshops() {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedMode, setSelectedMode] = useState("All");

  const [workshops, setWorkshops] = useState([]);
  const [featuredWorkshop, setFeaturedWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);

  // Truthful metrics derived from active published dataset
  const [totalPublishedCount, setTotalPublishedCount] = useState(0);
  const [activeDomainsCount, setActiveDomainsCount] = useState(0);

  // Load featured workshop and initial stats on mount
  useEffect(() => {
    let mounted = true;

    Promise.all([
      workshopsService.getFeaturedWorkshop(),
      workshopsService.getWorkshops(),
    ]).then(([featured, allPublished]) => {
      if (mounted) {
        setFeaturedWorkshop(featured);
        setTotalPublishedCount(allPublished.length);

        // Derive truthful count of technical domains present in data
        const uniqueDomains = new Set(allPublished.map((w) => w.category));
        setActiveDomainsCount(uniqueDomains.size);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Fetch filtered workshops via service layer whenever filter criteria change
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const timer = setTimeout(() => {
      workshopsService
        .getWorkshops({
          search,
          category: selectedCategory,
          mode: selectedMode,
        })
        .then((data) => {
          if (mounted) {
            setWorkshops(data);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error("Error fetching workshops:", err);
          if (mounted) setLoading(false);
        });
    }, 100); // 100ms debounce for smooth search input

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [search, selectedCategory, selectedMode]);

  const handleResetFilters = useCallback(() => {
    setSearch("");
    setSelectedCategory("All");
    setSelectedMode("All");
  }, []);

  const isFilterActive =
    Boolean(search.trim()) || selectedCategory !== "All" || selectedMode !== "All";

  return (
    <div className="w-full px-5 py-10 xl:px-10 2xl:px-14 max-w-7xl mx-auto">
      {/* 1. Technical Hero Section */}
      <WorkshopHero
        totalWorkshops={totalPublishedCount}
        domainCount={activeDomainsCount}
      />

      {/* 2. Featured / Upcoming Workshop Spotlight (shown when not actively searching) */}
      {!isFilterActive && featuredWorkshop && (
        <>
          <FeaturedWorkshop workshop={featuredWorkshop} />
          <Separator className="my-14 bg-white/10" />
        </>
      )}

      {/* 3. Filter Toolbar */}
      <Reveal delay={100}>
        <div id="workshop-catalog" className="pt-2">
          <WorkshopFilters
            search={search}
            onSearchChange={setSearch}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            selectedMode={selectedMode}
            onModeChange={setSelectedMode}
            totalCount={workshops.length}
            onReset={handleResetFilters}
          />
        </div>
      </Reveal>

      {/* 4. Workshop Grid or Loading / Empty States */}
      <div className="mt-8 min-h-[350px]">
        {loading ? (
          /* Loading Skeleton State */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 space-y-4 animate-pulse h-[420px]"
              >
                <div className="aspect-[16/9] w-full rounded-2xl bg-white/5" />
                <div className="h-4 w-1/3 rounded bg-white/5" />
                <div className="h-6 w-3/4 rounded bg-white/5" />
                <div className="h-4 w-full rounded bg-white/5" />
                <div className="h-4 w-2/3 rounded bg-white/5" />
              </div>
            ))}
          </div>
        ) : workshops.length === 0 ? (
          /* Empty State */
          <Reveal delay={0}>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-12 sm:p-16 text-center max-w-lg mx-auto backdrop-blur-xl shadow-xl space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 text-primary mx-auto flex items-center justify-center">
                <Search className="w-5 h-5" />
              </div>

              <h3 className="text-lg font-bold text-white">No workshops found</h3>

              <p className="text-xs text-white/60 leading-relaxed max-w-xs mx-auto">
                We couldn't find any workshops matching your search or selected filter options.
              </p>

              <div className="pt-2">
                <Button
                  onClick={handleResetFilters}
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-white/15 bg-white/[0.04] text-xs font-semibold text-white hover:bg-primary hover:text-black hover:border-primary transition-all duration-200 gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset All Filters</span>
                </Button>
              </div>
            </div>
          </Reveal>
        ) : (
          /* Active Workshops Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {workshops.map((workshop, idx) => (
              <Reveal key={workshop.id} delay={60 + (idx % 3) * 80}>
                <WorkshopCard workshop={workshop} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
